import axios from 'axios';
// eslint-disable-next-line import/no-cycle
import { APIS, clients } from '@/core/clients';

import helper from '@/core/helper';

const client = clients[APIS.SELF_SERVICE.name];

const campaignApi = {
  getCampaigns(params = '', advertiserId) {
    const url = advertiserId ? `/campaign/a/${advertiserId}/campaigns${params}` : `/campaign/campaigns${params}`;
    return client.get(url)
      .then((response) => response.data);
  },
  getCampaignsForGroup(payload) {
    return client.get(`/campaign/a/${payload.advertiserId}/campaigns/groups/${encodeURIComponent(payload.group)}`)
      .then((response) => response.data);
  },
  getCampaignGroups(params = '') {
    return client.get(`/campaign/campaigns/groups${params}`)
      .then((response) => response.data);
  },
  getCampaignGroupsForManaged(params = '') {
    return client.get(`/campaign/grouped-campaigns${params}`)
      .then((response) => response.data);
  },
  getSelfServiceCampaignGroups(params = '') {
    return client.get(`/campaign/grouped-campaigns/self-service${params}`)
      .then((response) => response.data);
  },
  getSharedCampaignGroups() {
    return client.get('/campaign/grouped-campaigns/shared')
      .then((response) => response.data);
  },
  getSharedCampaignsForReports(payload) {
    return client.get(`/campaign/campaigns/shared?campaignStates=${payload.filters.states.join(',')}&advertiserId=${payload.filters.advertiserId}&shareTypes=REPORTING,CAMPAIGN`)
      .then((response) => response.data);
  },
  getCampaign(payload) {
    return client.get(`/campaign/a/${payload.advertiserId}/campaigns/${payload.campaignId}`)
      .then((response) => response.data);
  },
  getSelfServiceCampaigns(params = '', managed) {
    return client.get(`/campaign/campaigns${(!managed ? '/self-service' : '') + params}`)
      .then((response) => response.data);
  },
  getCampaignNameById(id) {
    return client.get(`/campaign/campaigns/${id}`)
      .then((response) => response.data);
  },
  getAdvertisers(params = '') {
    return client.get(`/account/a${params}`)
      .then((response) => response.data);
  },
  getAdvertiser(payload) {
    return client.get(`/account/a/${payload}`)
      .then((response) => response.data);
  },
  createAdvertiser(payload) {
    return client.post('/account/a', { name: payload })
      .then((response) => response.data);
  },
  getContentProvider(payload) {
    return client.get(`/content-provider/a/${payload}/content-providers`)
      .then((response) => response.data);
  },
  createContentProvider(payload) {
    return client.post(`/content-provider/a/${payload.id}/content-providers`, {
      name: payload.name,
      type: payload.type,
    })
      .then((response) => response.data);
  },
  createCampaign(payload) {
    return client.post(`/campaign/a/${payload.advertiser.id}/campaigns`, { ...payload.body })
      .then((response) => response.data);
  },
  patchCampaign(advertiserId, campaignId, payload) {
    return client.patch(`/campaign/a/${advertiserId}/campaigns/${campaignId}`, payload)
      .then((response) => response.data);
  },
  getCampaignByName(payload) {
    return client.head(`/campaign/campaigns?name=${payload}`, { validateStatus: false })
      .then((response) => response);
  },
  getCurrencies() {
    return client.get('/currency/currencies')
      .then((response) => response.data);
  },
  createOrder(campaignId, payload) {
    return client.post(`/order/campaigns/${campaignId}/orders`, payload)
      .then((response) => response.data);
  },
  patchOrder(campaignId, orderId, payload) {
    return client.patch(`/order/campaigns/${campaignId}/orders/${orderId}`, { ...payload })
      .then((response) => response.data);
  },
  getOrder(advertiserId, campaignId) {
    return client.get(`/order/a/${advertiserId}/order?campaignId=${campaignId}`)
      .then((response) => response.data);
  },
  getOrders(advertiserId, campaignIds) {
    return client.get(`/order/a/${advertiserId}/orders?campaignIds=${campaignIds}`)
      .then((response) => response.data);
  },
  getOrdersMapped(campaignIds) {
    return client.get(`/order/orders/map?campaignIds=${campaignIds.join(',')}`)
      .then((response) => response.data);
  },
  getLinkingWords() {
    return axios
      .get('/resources/linking-words.txt')
      .then((response) => response.data.split('\n')
        .map((line) => line.trim()))
      .catch((error) => {
        throw error;
      });
  },
  getCountries() {
    return client.get('/country/countries')
      .then((response) => response.data);
  },
  getGeoLocations() {
    return client.get('/geo/geo-locations/countries')
      .then((response) => response.data);
  },
  getBlacklist(campaignId) {
    return client.get(`/brand-safety/campaigns/${campaignId}/blacklist-rule-set`)
      .then((response) => response.data);
  },
  patchBlacklist(campaignId, payload) {
    return client.patch(`/brand-safety/campaigns/${campaignId}/blacklist-rule-set`, { blacklistKeywords: payload })
      .then((response) => response.data);
  },
  submitCampaign(advertiserId, campaignId) {
    return client.patch(`/campaign/a/${advertiserId}/campaigns/${campaignId}/submit`)
      .then((response) => response.data);
  },
  copyCampaign(payload) {
    return client.post(`/campaign/a/${payload.advertiserId}/campaigns/${payload.campaignId}/copy`, { toCampaignName: payload.toCampaignName, copyTrackers: payload.copyTrackers, copyGroup: payload.copyGroup })
      .then((response) => response.data);
  },
  setCampaignAsDeleted(payload) {
    return client.patch(`/campaign/a/${payload.advertiserId}/campaigns/${payload.campaignId}/state`, { state: 'HIDDEN' })
      .then((response) => response.data);
  },
  setCampaignLive(payload) {
    return client.put(`/campaign/campaigns/${payload}/set-live`)
      .then((response) => response.data);
  },
  setCampaignState(payload) {
    return client.patch(`/campaign/a/${payload.advertiserId}/campaigns/${payload.campaignId}/state`, { state: payload.state })
      .then((response) => response.data);
  },
  setCampaignStateForSelfServiceDashboard(payload) {
    return client.put(`/campaign/campaigns/${payload.campaignId}/set-state`, { state: payload.state })
      .then((response) => response.data);
  },
  getGroupedStats(campaignIds) {
    return client.get(`/stats/grouped-stats?campaignIds=${campaignIds}`)
      .then((response) => response.data);
  },
  getPublishers(payload) {
    const queryParams = payload && payload.all ? '?all=true' : '';
    return client.get(`/publisher/publishers${queryParams}`)
      .then((response) => response.data);
  },
  getPublisherDeals() {
    return client.get('/publisher/publishers/deals')
      .then((response) => response.data);
  },
  getCreativeSizes() {
    return client.get('/creative/creative-sizes')
      .then((response) => response.data);
  },
  getPublisherGroups(payload) {
    return client.get(`/publisher/publisher-groups?type=${payload.type}`)
      .then((response) => response.data);
  },
  deleteCampaign(payload) {
    return client.delete(`/campaign/a/${payload.advertiserId}/campaigns/${payload.campaignId}`)
      .then((response) => response.data);
  },
  deleteOrder(payload) {
    return client.delete(`/order/campaigns/${payload.campaignId}/orders/${payload.orderId}`)
      .then((response) => response.data);
  },
  getCommonCounties() {
    return axios
      .get('/resources/common-counties.txt')
      .then((response) => response.data.split('\n')
        .map((line) => line.trim()))
      .catch((error) => {
        throw error;
      });
  },
  getAdxAdvertisers() {
    return axios
      .get('/resources/a.txt')
      .then((response) => helper.convertListToJson(response.data))
      .catch((error) => {
        throw error;
      });
  },
  getDealOverrides(payload) {
    return client.get(`/creative/a/${payload.advertiserId}/creatives/publisher-deals?orderId=${payload.orderId}`)
      .then((response) => response.data);
  },
  setCreativeDealOverrides(payload) {
    return client.put(`/creative/a/${payload.advertiserId}/creatives/publisher-deals`,
      payload.body)
      .then((response) => response.data);
  },
  postCreativeDealOverrides(payload) {
    return client.post(`/creative/a/${payload.advertiserId}/creatives/publisher-deals`,
      payload.body)
      .then((response) => response.data);
  },
  getAgencies() {
    return client.get('/account/agencies')
      .then((response) => response.data);
  },
  createAgency(payload) {
    return client.post('/account/agencies', { name: payload })
      .then((response) => response.data);
  },
  getPublishersCreative(payload) {
    return client.get(`/creative/a/${payload.advertiserId}/campaigns/${payload.campaignId}/creative-publishers`)
      .then((response) => response.data);
  },
  patchPublishersCreative(payload) {
    return client.patch(`/creative/a/${payload.advertiserId}/campaigns/${payload.campaignId}/creative-publishers`, payload.updatedValues)
      .then((response) => response);
  },
  getCampaignProperties(payload) {
    const { advertiserId, campaignId } = payload;
    return client.get(`/campaign/a/${advertiserId}/campaigns/${campaignId}/properties`)
      .then((response) => response);
  },
  getStates() {
    return client.get('/campaign/campaign-states')
      .then((response) => response);
  },
  uploadCampaignLogo(payload) {
    const { formData } = payload;
    const config = {
      ...client.defaults,
      headers: {
        ...client.defaults.headers,
        'Content-Type': 'multipart/form-data',
      },
    };
    return client.post(`/campaign/a/${payload.advertisersId}/campaigns/${payload.campaignId}/upload-file`, formData, config)
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  },
  patchCampaignProperties(payload) {
    const { advertiserId, campaignId, properties } = payload;
    return client.patch(`/campaign/a/${advertiserId}/campaigns/${campaignId}/properties`, { properties })
      .then((response) => response.data);
  },
  deleteLogo(payload) {
    const { advertiserId, campaignId } = payload;
    return client.delete(`/campaign/a/${advertiserId}/campaigns/${campaignId}/logo`)
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  },
  getAccess(campaignId) {
    return client.get(`/campaign/campaigns/${campaignId}/access`)
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  },
  updateCampaignGroup(payload) {
    const { advertiserId, group } = payload;
    return client.put(`/campaign/a/${advertiserId}/campaigns/groups/${encodeURIComponent(group)}`, payload.data)
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  },
  deleteCampaignGroup(payload) {
    const { advertiserId, group } = payload;
    return client.delete(`/campaign/a/${advertiserId}/campaigns/groups/${encodeURIComponent(group)}`)
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  },
  setDormantCampaignLive(payload) {
    return client.post(`/order/orders/${payload.campaignId}/set-live`)
      .then((response) => response.data)
      .catch((error) => {
        throw error;
      });
  },
};
export default campaignApi;
